/// <amd-module name="Build/Queue-Panel/PipelineResourcePanel" />
import * as React from "react";

import { PipelineResourceData, PipelineResourceRunData } from "Build/Client/Contracts/DataProvider";
import * as Resources from "Build/Queue-Panel/Resources";

import { QueueBuildStore } from "Build/Queue-Panel/Stores/QueueBuild";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { Ago } from "VSSUI/Ago";
import { Button } from "VSSUI/Button";
import { ListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import * as UIResources from "VSSUI/Resources.Input";
import { Spinner } from "VSSUI/Spinner";
import { IStatusProps, Status, Statuses, StatusSize } from "VSSUI/Status";
import { ColumnSelect, ITableColumn, ITableRow, SimpleTableCell, Table, TwoLineTableCell } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import * as TooltipEx from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import * as Utils_Date from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { Icon } from "VSSUI/Icon";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IPipelineResourcePanelProps {
    pageContext: IVssPageContext;
    store: QueueBuildStore;
    resource: PipelineResourceData;
    onUpdate(resource: PipelineResourceData): void;
    onDismiss(): void;
}

interface IPipelineResourcePanelState extends PipelineResourceData {
    searchText: string;
    useSelecteRunDisabled: boolean;
}

interface ITableItem extends PipelineResourceRunData {
}

const branchRefPrefix = "refs/heads/";
const tagRefPrefix = "refs/tags/";
const c_FetachingDelayInMs = 500;

export class PipelineResourcePanel extends React.Component<IPipelineResourcePanelProps, IPipelineResourcePanelState> {

    private _isLoading: IObservableValue<boolean>;
    private _selection: ListSelection = new ListSelection({ selectOnFocus: false, multiSelect: false});
    private _selectedRun: string;
    private _runs: IObservableArray<PipelineResourceRunData>;
    private _filteredRuns: IObservableArray<PipelineResourceRunData>;
    private _timerManagement: TimerManagement;

    constructor(props: IPipelineResourcePanelProps) {
        super(props);
        this._selectedRun = props.resource.version;
        this._isLoading = new ObservableValue(false);
        this._runs =  new ObservableArray([]);
        this._filteredRuns =  new ObservableArray([]);
        const state = { ...props.resource, searchText: "", useSelecteRunDisabled: false };
        this.state = state;
        this._timerManagement = new TimerManagement();
        this._searchPipelineRuns = this._timerManagement.debounce(this._searchPipelineRuns, c_FetachingDelayInMs);
    }

    public componentDidMount() {
        this._initializeResourceVersions();
    }
    
    public render(): JSX.Element {
        return (
            <Panel
                backButtonProps={{ onClick: this.props.onDismiss }}
                contentClassName="queue-panel-child no-padding"
                description={Resources.PipelineResourcePanelDescription}
                escDismiss={true}
                onDismiss={this.props.onDismiss}
                titleProps={{ text: this.props.resource.name }}
                className="pipeline-resource-panel"
                footerButtonProps={[
                    {
                        text: Resources.CancelButtonText,
                        onClick: this.props.onDismiss
                    },
                    {
                        text: Resources.PipelineResourceUpdateButtonText,
                        disabled: this.state.useSelecteRunDisabled,
                        primary: true,
                        onClick: this._update
                    }]}>
                <div className="margin-vertical-8 flex-grow">
                    {this._getSearchBar()}
                    {this._pipelineVersionsTableComponent()}
                </div>
            </Panel>
        );
    }

    public componentWillUnmount() {
        this.props.store.pipelineResourceRuns && this.props.store.pipelineResourceRuns.unsubscribe(this._updateResourceRuns);
    }

    private async _initializeResourceVersions() {
        this._fetchPipelineResourceRuns("");
    }

    private async _fetchPipelineResourceRuns(newValue: string) {
        this._isLoading.value = true;
        await this.props.store.fetchPipelineResourceRuns(this.state.project, this.state.alias, this.state.name, newValue, this.state.branch, this.state.tags);
        this._isLoading.value = false;

        this._updateResourceRuns();
        this.props.store.pipelineResourceRuns && this.props.store.pipelineResourceRuns.subscribe(this._updateResourceRuns);
    }

    private _pipelineVersionsTableComponent(): React.ReactNode {
        return (
                <div className={ "flex flex-grow flex-column"}>
                    <Observer loading={this._isLoading}  versions={this._filteredRuns}>
                        {(props: { loading: boolean, versions: PipelineResourceRunData[] }) => props.loading
                            ? (<div><Spinner className="queue-build-spinner" /></div>)
                            : (
                                <Observer versions={this._filteredRuns}>
                                    {(props: { versions: PipelineResourceRunData[] }) =>
                                        {
                                            if (props.versions.length > 0) {
                                                return (<Table
                                                            selection={this._selection} 
                                                            onSelect={(event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<PipelineResourceRunData>): void => {
                                                                let disableSelectedButton: boolean = false;
                                                                if (this._selection.selectedCount == 0) {
                                                                    disableSelectedButton = true;
                                                                }
                                                                this.setState({useSelecteRunDisabled: disableSelectedButton});
                                                                this._selectedRun = tableRow.data.runId;
                                                            }}
                                                            columns={this._tableColumns}
                                                            itemProvider={this._getItemProvider(props.versions)} />);
                                            } else {
                                                const message: string = this.state.searchText ? format(Resources.PipelineResourceNoSearchResultsText, this.state.searchText) :Resources.PipelineResourceNoRunsText;
                                                return (<div className="margin-vertical-8 padding-horizontal-20">
                                                            <MessageCard severity={MessageCardSeverity.Info}>{message}</MessageCard>
                                                        </div>);
                                            }
                                        }
                                    }
                                </Observer>
                            )}
                    </Observer>
                </div>
        );
    }

    private _renderRunCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        const runLine = (<TooltipEx.Tooltip text={tableItem.runId} overflowOnly={true}>
                            <span className="text-ellipsis primary-text">#{tableItem.runId}</span>
                        </TooltipEx.Tooltip>);

        let iconName = "OpenSource";
        let ref: string;
        if (tableItem.sourceBranch.startsWith(tagRefPrefix)) {
            iconName = "Tag";
            ref = tableItem.sourceBranch.replace(tagRefPrefix, "");
        } else {
            ref = tableItem.sourceBranch.replace(branchRefPrefix, "");
        }
        const branchAndCommitLine = <span className="fontSize secondary-text flex-row flex-baseline text-ellipsis">
                                        <Icon className="margin-right-4" iconName={iconName}/>
                                        <TooltipEx.Tooltip text={tableItem.sourceBranch} overflowOnly={true}>
                                            <span className="source-branch-content font-size text-ellipsis secondary-text">{ref}</span>
                                        </TooltipEx.Tooltip>
                                        <Icon className="margin-left-4" iconName={"BranchCommit"}/>
                                        <span>{tableItem.sourceVersion?.substring(0,8)}</span>
                                    </span>;
        return ( 
            <TwoLineTableCell 
                columnIndex={columnIndex}  
                tableColumn={tableColumn} 
                key={"col-" + columnIndex}
                iconProps={{ render: () => {
                    return (<Status
                    {... this._getStatusPropsForBuildResult(tableItem.result)}
                    key={tableItem.result}
                    size={StatusSize.l}
                    className="flex-self-center margin-right-8"
                />);
                }}}
                line1={runLine} 
                line2={branchAndCommitLine}/>);
        
    }

    private _renderFinishTimeCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span className="flex-row flex-center font-size-m secondary-text">
                    { tableItem.finishTime &&  <Ago date={tableItem.finishTime} format={Utils_Date.AgoFormat.Compact} /> }
                </span>
            </SimpleTableCell>
        );
    }

    private _getSearchBar(): JSX.Element {
        return (
            <TextField
                placeholder={Resources.PipelineResourceSearchBarText}
                className="margin-horizontal-20 margin-bottom-8"
                onChange={this._onChangeSearchText}
                prefixIconProps={{ iconName: "Search" }}
                suffixIconProps={
                    this.state.searchText
                    ?   {
                            render: className => {
                                return (
                                    <Button
                                        ariaLabel={UIResources.ClearFilter}
                                        className={css(className, "bolt-text-filterbaritem-clear")}
                                        iconProps={{ iconName: "Cancel"}}
                                        onClick={this._onClickClearButton}
                                    />
                                );
                            }
                        }
                    : undefined
                }
                value={this.state.searchText}
            />
        );
    }

    private _getItemProvider(runs: PipelineResourceRunData[]): ArrayItemProvider<ITableItem> {
        const rows: ITableItem[] = runs.map<ITableItem>(run => {
            return run as ITableItem;
        });
        return new ArrayItemProvider<ITableItem>(rows);
    }

    private _update = () => {
        const resource = { ...this.state };
        resource.version = this._selectedRun;
        this.props.onUpdate(resource);
        this.props.onDismiss();
    }

    private _onChangeSearchText = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string) => {
        this.setState({
            searchText: newValue,
            version: ""
        });
        this._searchPipelineRuns(newValue);
    }
    
    private _onClickClearButton = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        this.setState({
            searchText: "",
            version: ""
        });
        this._fetchPipelineResourceRuns("");
    };

    private _searchPipelineRuns = (searchValue: string): void => {
        this._fetchPipelineResourceRuns(searchValue);
    }

    private _updateSelection = (runs: PipelineResourceRunData[]): void => {
        let isRunSelected = false;
        if (this.state.version && this.state.version != "") {
            runs.some((run, index) => {
                if (equals(run.runId, this.state.version)) {
                    this._selection.select(index);
                    this.setState({
                        useSelecteRunDisabled: false
                    });
                    isRunSelected = true;    
                    return true;
                }
            });
        }
        //clear the previous selection as none of the runs match the version
        if (!isRunSelected) {
            this._selection.clear();
            
            if (runs.length > 0) {
                var selectionIndex: number;
                if (isFeatureFlagEnabled(this.props.pageContext, "Build2.ResolveDefaultResourceVersion", false)) {
                    //selecting the latest successful run (for specified branch/tags) as default in case of no version selected
                    selectionIndex = runs.findIndex(run => run.isDefaultSelection);
                } else {
                    //selecting the latest run as default in case of no version selected
                    selectionIndex = 0;
                }
                if (selectionIndex != -1) {
                    this._selection.select(selectionIndex);
                    this._selectedRun = runs[selectionIndex].runId;
                    this.setState({
                        version: runs[selectionIndex].runId,
                        useSelecteRunDisabled: false
                    });
                } else {
                    this.setState({useSelecteRunDisabled: true});
                }
            }
        }
    }

    private _updateResourceRuns = (): void => {
        const runs: PipelineResourceRunData[] = [];
        if (this.props.store.pipelineResourceRuns) {
            const pipelineRuns: PipelineResourceRunData[] | undefined = this.props.store.pipelineResourceRuns.get(this.state.alias);
            if (pipelineRuns && pipelineRuns.length >= 0) {
                runs.push(...pipelineRuns);
            }
        }

        this._runs.value = runs;
        this._filteredRuns.value = runs;
        this._updateSelection(runs);
    }

    private _getStatusPropsForBuildResult = (buildResult: string): IStatusProps => {
        switch(buildResult) {
            case "Succeeded":
                return Statuses.Success;
            case "PartiallySucceeded":
                return Statuses.Warning;
            case "Failed":
                return Statuses.Failed;
            case "Canceled":
                return Statuses.Canceled;
            default:
                return Statuses.Queued;
        }
    }

    private _tableColumns: ITableColumn<ITableItem>[] = [
        new ColumnSelect(), {
            id: "pipeline-run",
            width: 300,
            name: Resources.PipelineRunColumnHeadingForPipelineResourcePanel,
            renderCell: this._renderRunCell
        },
        {
            id: "completed",
            width: 104,
            name: Resources.CompletedColumnHeadingForPipelineResourcePanel,
            renderCell: this._renderFinishTimeCell
        }
    ];
}
